import { styled } from '@mui/material/styles';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import React, { memo, useCallback, useRef } from 'react';

import CustomPickersDay from '../CustomPickersDay';

// Original class: calendarRoot
const StyledCalendarRoot = styled(({ renderDay, ...props }) => (
  <DateCalendar {...props} />
))(({ theme }) => ({
  width: 'fit-content !important',
  borderRadius: theme.spacing(4),
  border: '1px solid #DCDFF4',
  margin: '0 !important',
  marginRight: `${theme.spacing(4)} !important`,
  marginBottom: `${theme.spacing(4)} !important`,
  height: 308,

  '& > div': {
    padding: 0,
    margin: 0,
    overflow: 'hidden',
  },

  '& [role="presentation"]': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 30,
    //backgroundColor: '#DCDFF4',
    padding: theme.spacing(1, 0),
  },

  '& .PrivatePickersFadeTransitionGroup-root': {
    textTransform: 'capitalize',
    overflowY: 'hidden',
    ...theme.typography.h3,
    fontWeight: 700,
  },

  '& .PrivatePickersSlideTransition-root': {
    backgroundColor: 'transparent',
  },

  '& .MuiTypography-caption': {
    width: 44,
    margin: 0,
    textTransform: 'lowercase',
    color: '#41424E',
    ...theme.typography.h5,
  },

  '& .MuiButtonBase-root.MuiIconButton-root': {
    display: 'none',
  },

  '& .MuiButtonBase-root.MuiPickersDay-root': {
    display: 'block',
    maxWidth: 44,
    width: '100%',
    borderRadius: 0,
    margin: 0,
    ...theme.typography.h4,
  },

  '& [role=row]': {
    margin: 0,
    backgroundColor: '#ffffff',
  },

  '& .MuiPickersArrowSwitcher-root': {
    display: 'none',
  },
  [theme.breakpoints.down('lg')]: {
    marginRight: '16px !important',
    marginLeft: '0 !important',
    marginBottom: theme.spacing(4),
  },
  [theme.breakpoints.down('xs')]: {
    marginRight: '0 !important',
  },
  '& .MuiPickersCalendarHeader-root': {
    backgroundColor: '#DCDFF4',
    minHeight: '30px',
  },
}));

const CustomCalendar = ({
  year,
  month,
  customDays,
  handleToggleExclusionMenu,
  getCurrentDay,
  setFieldValue,
  workHours,
}) => {
  const { current: getDateColor } = useRef((date, days) => {
    const currentDayObj = getCurrentDay(date, days);
    if (date.$M !== dayjs(month, 'MM').get('month')) {
      return { color: '#E4E4EF' };
    }
    if (!currentDayObj) {
      return { color: '#212346' };
    }
    if (currentDayObj.isExclusion) {
      return { color: '#EB2FFC' };
    }
    if (currentDayObj.isDayOff) {
      return { color: '#E03737' };
    }
  });

  const renderPickerDay = useCallback(
    (date, selectedDates, pickersDayProps) => {
      const currentDayObj = getCurrentDay(date, customDays);
      const onDaySelect = selectedDate => {
        const currentDayObj = getCurrentDay(selectedDate, customDays);
        setFieldValue(
          'hours',
          typeof currentDayObj.hours === 'number'
            ? currentDayObj.hours
            : workHours,
        );
        handleToggleExclusionMenu(selectedDate);
      };
      return (
        <CustomPickersDay
          date={dayjs(currentDayObj.date).format()}
          value={dayjs(currentDayObj.date).format()}
          key={currentDayObj.date}
          style={getDateColor(date, customDays)}
          currentDayObj={currentDayObj}
          onDaySelect={onDaySelect}
        />
      );
    },
    [
      customDays,
      getCurrentDay,
      getDateColor,
      handleToggleExclusionMenu,
      setFieldValue,
      workHours,
    ],
  );

  const CustomDay = memo(
    ({
      day,
      customDays,
      getCurrentDay,
      getDateColor,
      handleToggleExclusionMenu,
      setFieldValue,
      workHours,
      ...other
    }) => {
      const currentDayObj = getCurrentDay(day, customDays);

      const onDaySelect = selectedDate => {
        const currentDayObj = getCurrentDay(selectedDate, customDays);
        setFieldValue(
          'hours',
          typeof currentDayObj.hours === 'number'
            ? currentDayObj.hours
            : workHours,
        );
        handleToggleExclusionMenu(selectedDate);
      };

      return (
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={false}
          disableRipple
          style={getDateColor(day, customDays)}
          isdayoff={`${currentDayObj.isDayOff}`}
          onClick={() => onDaySelect(day)}
        />
      );
    },
  );
  return (
    <StyledCalendarRoot
      dayOfWeekFormatter={day => `${day.format('dd')}`}
      readOnly={true}
      value={month ? dayjs(month, 'MM').year(year) : null}
      slots={{
        day: props => (
          <CustomDay
            {...props}
            customDays={customDays}
            getCurrentDay={getCurrentDay}
            getDateColor={getDateColor}
            handleToggleExclusionMenu={handleToggleExclusionMenu}
            setFieldValue={setFieldValue}
            workHours={workHours}
          />
        ),
      }}
      showDaysOutsideCurrentMonth
      view='day'
      onChange={e => e.preventDefault()}
    />
  );
};

export default memo(CustomCalendar);
